import { createTheme } from '@mui/material/styles';

/**
 * The MUI theme mirrors `tokens.css` so application chrome (MUI) and the spatial
 * world (custom CSS) share one palette. Colour schemes follow the OS preference
 * through the `media` selector, exactly like the CSS tokens.
 */
const fontUi = "'Iowan Old Style', 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif";
const fontMono = "ui-monospace, 'SF Mono', Menlo, Consolas, monospace";

export const theme = createTheme({
  cssVariables: { colorSchemeSelector: 'media' },
  colorSchemes: {
    light: {
      palette: {
        mode: 'light',
        primary: { main: '#b5522a' },
        error: { main: '#9a3b2e' },
        background: { default: '#ece6da', paper: '#f9f6ef' },
        text: { primary: '#1f1c18', secondary: '#5c554b', disabled: '#8d8578' },
        divider: 'rgba(31, 28, 24, 0.12)',
        action: { hover: 'rgba(31, 28, 24, 0.06)', selected: 'rgba(181, 82, 42, 0.18)', disabledOpacity: 0.45 },
      },
    },
    dark: {
      palette: {
        mode: 'dark',
        primary: { main: '#d4784f' },
        error: { main: '#d16a5a' },
        background: { default: '#221f1b', paper: '#2c2823' },
        text: { primary: '#ece6da', secondary: '#b9b1a3', disabled: '#8d8578' },
        divider: 'rgba(236, 230, 218, 0.12)',
        action: { hover: 'rgba(236, 230, 218, 0.08)', selected: 'rgba(212, 120, 79, 0.22)', disabledOpacity: 0.45 },
      },
    },
  },
  shape: { borderRadius: 6 },
  spacing: 4,
  typography: {
    fontFamily: fontUi,
    fontSize: 13,
    button: { textTransform: 'none', fontWeight: 500, fontSize: 13 },
    body2: { fontSize: 13 },
    caption: { fontSize: 11.5, fontFamily: fontMono },
    overline: { fontSize: 10.5, letterSpacing: '0.08em' },
    subtitle2: { fontSize: 11.5, fontWeight: 600 },
  },
  components: {
    MuiButtonBase: { defaultProps: { disableRipple: true } },
    MuiButton: {
      defaultProps: { size: 'small', disableElevation: true },
      styleOverrides: {
        root: { minHeight: 28, paddingInline: 10, whiteSpace: 'nowrap' },
        startIcon: { marginRight: 6, '& > *:first-of-type': { fontSize: 18 } },
      },
    },
    MuiIconButton: {
      defaultProps: { size: 'small' },
      // Reads the CSS tap-target token so touch layouts get 44px without a second theme.
      styleOverrides: { root: { borderRadius: 4, minWidth: 'var(--tap-target)', minHeight: 'var(--tap-target)' } },
    },
    MuiTooltip: {
      defaultProps: { arrow: false, enterDelay: 500, disableInteractive: true },
      styleOverrides: { tooltip: { fontFamily: fontUi, fontSize: 12 } },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ theme: t }) => ({
          border: `1px solid ${t.palette.divider}`,
          backgroundImage: 'none',
        }),
      },
    },
    MuiTextField: { defaultProps: { size: 'small', variant: 'outlined' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { minHeight: 28, fontSize: 13 },
        input: { paddingBlock: 4, paddingInline: 8 },
      },
    },
    MuiNativeSelect: { styleOverrides: { select: { paddingBlock: 4, paddingInline: 8, fontSize: 13 } } },
    MuiCheckbox: { defaultProps: { size: 'small' }, styleOverrides: { root: { padding: 4 } } },
    MuiChip: { defaultProps: { size: 'small' }, styleOverrides: { label: { fontFamily: fontMono, fontSize: 10.5 } } },
    MuiDialog: {
      defaultProps: { transitionDuration: 120 },
      styleOverrides: { paper: { borderRadius: 10 } },
    },
    MuiListItemButton: { styleOverrides: { root: { borderRadius: 6, paddingBlock: 6 } } },
    MuiDivider: { styleOverrides: { root: { marginInline: 4 } } },
  },
});

export const monoFont = fontMono;
